import styled, {useTheme} from 'styled-components/native';
import TextGeneric from '../../../components/textGeneric';
import {Theme} from '../../../theme/lightTheme';
import {getSettingByKey} from '../../../utils/helpers';
import {Dimensions, Linking, Platform, View} from 'react-native';
import Config from 'react-native-config';
import {tr} from '../../../translations';
import {ScreenProps} from '../../../interfaces/screens/mainScreen';
import React from 'react';
import SupportItem from '../../../components/supportItem';
import {modalTypes, SettingKey, ToastType} from '../../../utils/enums';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {openQuestionModal} from '../../../connected-components/questionModal/actions';
import {showGlobalToast} from '../../../connected-components/toast/actions';
import Icon from '../../../components/icon';
import MyPressable from '../../../components/myPressable';

const isWeb = Platform.OS === 'web';

const Container = styled.View<{width: string}>`
  padding-top: 5%;
  width: ${({width}) => width};
  flex-grow: 1;
  ${!isWeb &&
  `
    height: ${Dimensions.get('screen').height}px;
  `}
  ${isWeb &&
  `
    height: 100%;
    min-height: 400px;
    overflow: scroll;
  `}
`;

const ContentWrapper = styled.View`
  ${({theme}) =>
    isWeb &&
    `
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
    padding: 0 ${(theme as Theme).text.s55}px;
  `}
`;

const Title = styled(TextGeneric)`
  font-size: ${({theme}) => (theme as Theme).text.s5}px;
  letter-spacing: 0.5px;
  color: ${({theme}) => (theme as Theme).text.dark};
  text-align: center;
  padding-horizontal: 5%;
  margin-bottom: 8px;
  font-weight: 600;
  line-height: 24px;
`;

const WebTitle = styled(Title)`
  font-size: ${({theme}) => (theme as Theme).text.s4}px;
  line-height: 28px;
  margin-bottom: 12px;
`;

const Subtitle = styled(TextGeneric)`
  font-size: ${({theme}) => (theme as Theme).text.s8}px;
  color: ${({theme}) => (theme as Theme).text.grey};
  text-align: center;
  padding-horizontal: 10%;
  margin-bottom: 24px;
  line-height: 18px;
`;

const WebSubtitle = styled(Subtitle)`
  font-size: ${({theme}) => (theme as Theme).text.s7}px;
  line-height: 20px;
  margin-bottom: 32px;
`;

const ContactItemsContainer = styled.View`
  padding-top: 5%;
  height: 100%;
  align-items: flex-start;
  justify-content: flex-start;
  ${isWeb &&
  `
    margin: 0 -6%;
  `}
`;

const Separator = styled.View`
  width: 100%;
  align-self: center;
  height: 1px;
  background-color: ${({theme}) => (theme as Theme).border + '20'};
  margin: 8px 0;
`;

const EmailDisplayContainer = styled.View<{isWeb: boolean}>`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-horizontal: 16px;
  padding-vertical: 12px;
  margin-bottom: 6px;
  border-radius: 10px;
  background-color: ${({theme}) => (theme as Theme).screenBackground};
  ${({isWeb, theme}) =>
    isWeb &&
    `
    padding-horizontal: 20px;
    padding-vertical: 16px;
    margin-bottom: 10px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid ${(theme as Theme).border + '20'};
  `}
`;

const EmailLeftContainer = styled.View`
  flex-direction: row;
  flex: 1;
  align-items: center;
  justify-content: flex-start;
`;

const EmailIconContainer = styled.View`
  background-color: ${({theme}) => (theme as Theme).secondary + '15'};
  border-radius: 10px;
  align-items: center;
  justify-content: center;
  padding: 10px;
  margin-right: 12px;
  min-width: 40px;
  min-height: 40px;
  ${isWeb &&
  `
    padding: 12px;
    border-radius: 12px;
    margin-right: 16px;
    min-width: 48px;
    min-height: 48px;
  `}
`;

const EmailContentContainer = styled.View`
  flex: 1;
  justify-content: center;
`;

const EmailTitle = styled(TextGeneric)`
  font-size: 14px;
  font-weight: 500;
  color: ${({theme}) => (theme as Theme).text.dark};
  margin-bottom: 2px;
  line-height: 18px;
  ${isWeb &&
  `
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 4px;
    line-height: 20px;
  `}
`;

const EmailAddress = styled(TextGeneric)`
  font-size: 13px;
  color: ${({theme}) => (theme as Theme).text.grey};
  line-height: 16px;
  ${isWeb &&
  `
    font-size: 14px;
    line-height: 18px;
  `}
`;

const CopyButton = styled(MyPressable)<{isWeb: boolean}>`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: ${({theme}) => (theme as Theme).secondary + '15'};
  border-radius: 8px;
  padding-horizontal: 12px;
  padding-vertical: 8px;
`;

/**
 * Support screen component with enhanced design and dedicated SupportItem components.
 *
 * @param {ScreenProps} props - The screen component props.
 * @param {object} props.navigation - The navigation object.
 */
const Support: React.FC<ScreenProps> = () => {
  const theme = useTheme() as Theme;
  const {wdp, isMobile, hdp, isLargeScreen} = useResponsiveDimensions();
  const isWeb = Platform.OS === 'web';
  
  const copyEmailToClipboard = async () => {
    const email = getSettingByKey(SettingKey.EmailUs) || Config.EMAIL;
    try {
      if (isWeb && typeof navigator !== 'undefined' && navigator.clipboard) {
        // Use modern Clipboard API for web
        await navigator.clipboard.writeText(email);
        showGlobalToast({
          message: tr('app.copy'),
          type: ToastType.SUCCESS,
        });
      } else if (isWeb && typeof document !== 'undefined') {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = email;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          showGlobalToast({
            message: tr('app.copy'),
            type: ToastType.SUCCESS,
          });
        } catch (err) {
          openQuestionModal({
            title: `Contact us via email: ${email}`,
            message: ``,
            type: modalTypes.info,
          });
        }
        document.body.removeChild(textArea);
      } else {
        // For mobile platforms, use React Native Clipboard
        const {Clipboard} = require('react-native');
        Clipboard.setString(email);
        showGlobalToast({
          message: tr('app.copy'),
          type: ToastType.SUCCESS,
        });
      }
    } catch (error) {
      const email = getSettingByKey(SettingKey.EmailUs) || Config.EMAIL;
      openQuestionModal({
        title: `Contact us via email: ${email}`,
        message: ``,
        type: modalTypes.info,
      });
    }
  };

  const mailUs = (e?: any) => {
    const email = getSettingByKey(SettingKey.EmailUs) || Config.EMAIL;
    try {
      // For mobile platforms, use Linking.openURL
      Linking.openURL(`mailto:${email}?subject=Support`);
    } catch (error) {
      openQuestionModal({
        title: `Contact us via email: ${email}`,
        message: ``,
        type: modalTypes.info,
      });
    }
  };

  const callUs = () => {
    const phone = getSettingByKey(SettingKey.CallUs) || Config.PHONE_NUMBER;
    let phoneNumber = '';
    if (Platform.OS === 'ios') {
      phoneNumber = `telprompt:${phone}`;
    } else {
      phoneNumber = `tel:${phone}`;
    }
    try {
      Linking.openURL(phoneNumber);
    } catch (e) {
      openQuestionModal({
        title: `Reach us by phone: ${phone}`,
        message: ``,
        type: modalTypes.info,
      });
    }
  };

  const contactUsItems = [
    {
      title: tr('contactUs.emailTitle'),
      subtitle: tr('contactUs.emailSubtitle') || 'Send us an email and we\'ll get back to you',
      iconName: 'email',
      iconColor: theme.secondary,
      iconBackgroundColor: theme.secondary + '15',
      onPress: mailUs,
    },
  ];

  // Choose appropriate title and subtitle components based on platform
  const TitleComponent = isWeb && !isMobile ? WebTitle : Title;
  const SubtitleComponent = isWeb && !isMobile ? WebSubtitle : Subtitle;

  return (
    <Container width={isWeb ? '100%' : `${wdp(100)}px`}>
      <ContentWrapper>
        <TitleComponent>{tr('contactUs.title')}</TitleComponent>
        <SubtitleComponent>
          {tr('contactUs.subtitle') || 'We\'re here to help! Choose your preferred way to get in touch with our support team.'}
        </SubtitleComponent>
        <ContactItemsContainer>
          {contactUsItems.map((item, index) => {
            const email = getSettingByKey(SettingKey.EmailUs) || Config.EMAIL;
            
            // On web, show email with copy button instead of mailto link
            if (isWeb && item.iconName === 'email') {
              return (
                <View key={index} style={{width: '100%'}}>
                  {index !== 0 && isMobile && <Separator />}
                  <EmailDisplayContainer isWeb={isWeb}>
                    <EmailLeftContainer>
                      <EmailIconContainer>
                        <Icon
                          name={item.iconName}
                          size={isWeb && !isMobile ? 22 : 18}
                          color={item.iconColor}
                        />
                      </EmailIconContainer>
                      <EmailContentContainer>
                        <EmailTitle>{item.title}</EmailTitle>
                        <EmailAddress>{email}</EmailAddress>
                      </EmailContentContainer>
                    </EmailLeftContainer>
                    <CopyButton backgroundColor={theme.secondary} onPress={copyEmailToClipboard} iosOpacityEffect isWeb={isWeb}>
                      <Icon
                        name="copy"
                        size={isWeb && !isMobile ? 16 : 14}
                        color="#FFFFFF"
                        stroke="#FFFFFF"
                      />
                    </CopyButton>
                  </EmailDisplayContainer>
                </View>
              );
            }
            
            // For mobile or other items, use SupportItem
            return (
              <View key={index} style={{width: '100%'}}>
                {index !== 0 && isMobile && <Separator />}
                <SupportItem
                  title={item.title}
                  subtitle={item.subtitle}
                  iconName={item.iconName}
                  iconColor={item.iconColor}
                  iconBackgroundColor={item.iconBackgroundColor}
                  onPress={item.onPress}
                />
              </View>
            );
          })}
        </ContactItemsContainer>
      </ContentWrapper>
    </Container>
  );
};

export default Support;
